<template>
  <div class="team-members-ctn">
    <h2 class="header-text">{{ $t('profile.menu.team_members') }}</h2>
    <div class="d-flex flex-wrap flex-sm-nowrap justify-space-between align-center mt-5">
      <div class="d-flex flex-column flex-sm-row align-start align-sm-center mb-3 mb-sm-0">
        <v-btn
          @click="handleInviteUserDialog"
          class="primary mb-2 mb-sm-0 mr-sm-4"
          :disabled="hasExceededSeats">
          {{ $t('profile.team_members_invite_a_new_user') }}
        </v-btn>
        <div v-if="hasExceededSeats">
          <span class="primary--text" v-html="$t('profile.team_members_upgrade_more_seats')"></span>
        </div>
      </div>
      <div>
        <span class="text-active">
          {{ formatNumber(usedMembersCount) }} {{$t('used')}}
        </span>
        /
        <span class="text-total">
          {{ formatNumber(totalMembersCount) }} {{ $t('total') }}
        </span>
      </div>
  </div>
    <!--  table  -->
    <div class="team-members-table mt-5">
      <v-row class="team-members-table-header font-light pa-0 ma-0 d-none d-sm-flex">
        <v-col cols="3" class="d-flex flex-start">{{ $t('profile.team_members_name') }}</v-col>
        <v-col cols="3" class="d-flex justify-center">{{ $t('profile.team_members_email') }}</v-col>
        <v-col cols="2" class="d-flex justify-center">{{ $t('profile.team_members_role') }}</v-col>
        <v-col cols="2" class="d-flex justify-center">{{ $t('profile.team_members_status') }}</v-col>
        <v-col cols="2" class="d-flex justify-center">{{ $t('profile.team_members_actions') }}</v-col>
      </v-row>
      <v-row class="pa-0 ma-0 team-members-table-item d-none d-sm-flex" v-for="member in teamMembers" :key="member.id"
        align="center">
        <v-col cols="3">
          <div class="breakable-text">
            <span class="font-medium mr-2 text-center">{{ fullName(member.first_name, member.last_name) }}</span>
          </div>
        </v-col>
        <v-col cols="3" class="text-center">
          <div class="breakable-text">{{ member.email }}</div>
        </v-col>
        <v-col cols="2" class="d-flex justify-center">{{ transformRole(member.role) }}</v-col>
        <v-col cols="2" class="d-flex justify-center">{{  transformStatus(member.is_active) }}</v-col>
        <v-col cols="2" class="text-center">
          <v-menu bottom left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" color="primary">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="resetPassword(member)" link>
                <v-list-item-title>{{ $t('profile.team_members_action_reset_password') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="editUser(member)" link>
                <v-list-item-title>{{ $t('profile.team_members_action_edit_user') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="removeUser(member)" link>
                <v-list-item-title>{{ $t('profile.team_members_action_remove_user')
                  }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>

      <!-- mobile view display-->
      <div class="d-block d-sm-none">
        <v-card v-for="member in teamMembers" :key="'mobile-' + member.id" class="mb-3 pa-3" outlined>
          <div class="d-flex justify-space-between mb-2">
            <span>{{ $t('profile.team_members_name') }}</span>
            <strong>{{ fullName(member.first_name, member.last_name)}}</strong>
          </div>
          <div class="d-flex justify-space-between mb-2">
            <span>{{ $t('profile.team_members_email') }}</span>
            <span>{{ member.email }}</span>
          </div>
          <div class="d-flex justify-space-between mb-2">
            <span>{{ $t('profile.team_members_role') }}</span>
            <span>{{ transformRole(member.role) }}</span>
          </div>
          <div class="d-flex justify-space-between mb-2">
            <span>{{ $t('profile.team_members_status') }}</span>
            <span>{{ transformStatus(member.is_active) }}</span>
          </div>
          <div class="d-flex justify-space-between align-center">
            <span>{{ $t('profile.team_members_actions') }}</span>
            <v-menu bottom left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" color="primary">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="resetPassword(member)" link>
                  <v-list-item-title>{{ $t('profile.team_members_action_reset_password') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="editUser(member)" link>
                  <v-list-item-title>{{ $t('profile.team_members_action_edit_user') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="removeUser(member)" link>
                  <v-list-item-title>{{ $t('profile.team_members_action_remove_user')
                    }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </v-card>
      </div>
    </div>
  <invite-user-dialog
    :display-invite-user-dialog="displayInviteUserDialog"
    @update:displayInviteUserDialog="handleCloseDialog"
    :is-editing.sync="isEditing"
    :selected-user.sync="selectedMember"/>
  <confirmation-dialog
    :display-confirmation-dialog="displayConfirmationDialog"
    :title="confirmationDialogTitle"
    :confirmation-message="confirmationDialogMessage"
    :content="confirmationDialogContent"
    @update:displayConfirmationDialog="closeConfirmationDialog"
    @handle-confirmation="handleRemoveMemberConfirmation"
  />
  </div>
</template>

<script>
import InviteUserDialog from '@/components/profile/dialog/InviteUserDialog.vue'
import ConfirmationDialog from '@/components/profile/dialog/ConfirmationDialog.vue'
import { mapGetters } from 'vuex'
import Utils from '@/utils/utils'

export default {
  name: 'TeamMembers',
  components: {
    InviteUserDialog,
    ConfirmationDialog
  },
  data: () => ({
    displayInviteUserDialog: false,
    displayConfirmationDialog: false,
    confirmationDialogTitle: '',
    confirmationDialogMessage: '',
    confirmationDialogContent: '',
    selectedMember: null,
    isEditing: false
  }),
  computed: {
    ...mapGetters(['teamMembers', 'totalMembersCount', 'usedMembersCount']),
    hasExceededSeats () {
      return this.usedMembersCount >= this.totalMembersCount
    }
  },
  methods: {
    handleInviteUserDialog () {
      this.displayInviteUserDialog = true
    },
    handleCloseDialog () {
      this.displayInviteUserDialog = false
      if (this.selectedMember) {
        this.selectedMember = null
      }
      this.isEditing = false
    },
    goToUpgrade () {
      this.$router.push({ name: 'upgrade', query: { source: 'team_members' } })
    },
    async resetPassword (member) {
      try {
        const payload = { id: member.id }
        await this.$store.dispatch('resetTeamMemberPassword', payload)
        await this.$store.dispatch('showSnackBar', { message: this.$t('profile.team_members_password_reset') })
      } catch (err) {
        console.log(err)
      }
    },
    editUser (member) {
      this.displayInviteUserDialog = true
      this.isEditing = true
      this.selectedMember = member
    },
    async removeUser (member) {
      try {
        this.selectedMember = member
        this.updateConfirmationDialog(
          this.$t('profile.team_members_action_remove_user'),
          this.$t('profile.team_members_remove_user_message'),
          this.$t('profile.team_members_remove_user_content')
        )
        this.displayConfirmationDialog = true
      } catch (err) {
        console.log(err)
      }
    },
    transformStatus (isActive) {
      if (isActive) return this.$t('profile.team_members_status_active')
      else return this.$t('profile.team_members_status_inactive')
    },
    fullName (firstName, lastName) {
      return firstName + ' ' + lastName
    },
    transformRole (role) {
      if (role === 'brand_account_admin') return this.$t('profile.team_members_admin')
      else return this.$t('profile.team_members_member')
    },
    updateConfirmationDialog (title, message, content) {
      this.confirmationDialogMessage = message
      this.confirmationDialogContent = content
      this.confirmationDialogTitle = title
    },
    resetConfirmationDialog () {
      this.confirmationDialogMessage = ''
      this.confirmationDialogContent = ''
      this.confirmationDialogTitle = ''
    },
    closeConfirmationDialog () {
      this.displayConfirmationDialog = false
      if (this.selectedMember) {
        this.selectedMember = null
      }
    },
    async handleRemoveMemberConfirmation () {
      const payload = { id: this.selectedMember.id }
      await this.$store.dispatch('removeTeamMember', payload)
      await this.$store.dispatch('showSnackBar', { message: this.$t('profile.team_members_user_removed') })
      this.selectedMember = null
      this.closeConfirmationDialog()
      this.resetConfirmationDialog()
    },

    formatNumber (value) {
      return Utils.formatNumber(value, this.$i18n.locale)
    }
  },
  async created () {
    try {
      await this.$store.dispatch('loadTeamMembers')
    } catch (error) {
      console.log(error)
    }
  }
}
</script>

<style scoped lang="sass">
@import "@/variables.sass"

@mixin text-usage
  color: #000
  font-size: 18px
  font-style: normal
  font-weight: 400
  line-height: normal

.team-members-ctn
  min-height: calc(100vh - var(--banner-height, 0px))
  height: auto
  max-height: none
  overflow-y: auto
  scrollbar-width: none
  -ms-overflow-style: none
  -webkit-overflow-scrolling: touch

  &::-webkit-scrollbar
    display: none

  .header-text
    color: #242424
    font-size: 26px
    font-style: normal
    font-weight: 500
    line-height: 24px

  .text-total
    @include text-usage

  .text-active
    @include text-usage
    color: $primary

@media (max-width: 960px)
  .team-members-ctn
    overflow-y: visible
    max-height: none
    padding-bottom: 72px

.cursor-pointer
  cursor: pointer

.team-members-table
  table-layout: fixed
  width: 100%

  .team-members-table-header
    border-radius: 6px 0 0 0
    border: 1px solid rgba(0, 0, 0, 0.10)
    background: #F3F3F3

  .team-members-table-item
    border-bottom: 1px solid rgba(0, 0, 0, 0.10)
    border-left: 1px solid rgba(0, 0, 0, 0.10)
    border-right: 1px solid rgba(0, 0, 0, 0.10)
    background: #FFF

.breakable-text
  overflow-wrap: anywhere
  word-break: break-word

.d-block.d-sm-none
  .v-card
    .d-flex
      span:last-child, strong:last-child
        word-wrap: break-word
        overflow-wrap: break-word
        text-align: right
        max-width: 60%
</style>
